import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {registerDialogHost} from '../confirm';

const DANGER = /удал|безвозвр|сброс|заменить|перезапис/i;

// Единый вид подтверждений и сообщений вместо окон браузера (window.confirm / alert): подключается один раз в App.
// Вызовы — confirmDialog(текст, опции) → Promise<boolean> и alertDialog(текст) из ../confirm.
const ConfirmHost = () => {
    const [item, setItem] = useState(null);
    const resolveRef = useRef(null);

    const show = useCallback((request) => new Promise((resolve) => {
        // Если диалог уже открыт, предыдущий считаем отменённым.
        if (resolveRef.current) resolveRef.current(false);
        resolveRef.current = resolve;
        setItem(request);
    }), []);

    useEffect(() => {
        registerDialogHost(show);
        return () => registerDialogHost(null);
    }, [show]);

    const close = (result) => {
        const resolve = resolveRef.current;
        resolveRef.current = null;
        setItem(null);
        if (resolve) resolve(result);
    };

    const alertOnly = item && item.kind === 'alert';
    const danger = item && !alertOnly && (item.danger ?? DANGER.test(item.message));
    const Icon = alertOnly ? InfoOutlinedIcon : danger ? ErrorOutlineIcon : HelpOutlineIcon;
    const title = item && (item.title || (alertOnly ? 'Сообщение' : danger ? 'Подтвердите действие' : 'Подтверждение'));

    return (
        <Dialog open={!!item} onClose={() => close(false)} maxWidth="xs" fullWidth>
            {item && (
                <>
                    <DialogTitle sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                        <Box sx={{
                            width: 40, height: 40, borderRadius: '50%', display: 'grid', placeItems: 'center', flexShrink: 0,
                            bgcolor: (t) => `${danger ? t.palette.error.main : t.palette.primary.main}22`,
                            color: danger ? 'error.main' : 'primary.main',
                        }}>
                            <Icon/>
                        </Box>
                        {title}
                    </DialogTitle>
                    <DialogContent>
                        <Typography color="text.secondary" sx={{whiteSpace: 'pre-line', lineHeight: 1.6}}>{item.message}</Typography>
                    </DialogContent>
                    <DialogActions>
                        {!alertOnly && <Button color="inherit" onClick={() => close(false)}>{item.cancelText || 'Отмена'}</Button>}
                        <Button variant="contained" color={danger ? 'error' : 'primary'} autoFocus onClick={() => close(true)}>
                            {item.confirmText || (alertOnly ? 'Понятно' : danger ? 'Подтвердить' : 'Да')}
                        </Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

export default ConfirmHost;
